import React from 'react';
import { computeBillTotals, inr } from '../../utils/billing';

/**
 * Payment breakdown shown under both booking forms.
 * Totals come from the shared `computeBillTotals`, so the quoted figure matches the one
 * the server calculates.
 */
const PriceSummary = ({
  subtotal,
  gstPercentage = 0,
  gstEnabled = true,
  extraCharges = 0,
  discount = 0,
  label,
  totalLabel = 'Estimated Total',
  note
}) => {
  if (!subtotal) return null;

  const bill = computeBillTotals({
    subtotal,
    extraCharges,
    discount,
    gstPercentage: gstEnabled ? gstPercentage : 0
  });

  const Row = ({ children, value, tone = 'text-railway-600', valueTone = 'text-railway-700' }) => (
    <div className="flex items-center justify-between text-sm">
      <span className={tone}>{children}</span>
      <span className={`font-medium ${valueTone}`}>{value}</span>
    </div>
  );

  return (
    <div className="bg-gradient-to-r from-railway-50 to-railway-100 rounded-xl p-4 border border-railway-200">
      <h4 className="text-sm font-semibold text-railway-800 mb-2.5">Payment Summary</h4>
      <div className="space-y-1.5">
        <Row value={inr(bill.subtotal)}>{label || 'Room charges'}</Row>

        {bill.extraCharges > 0 && <Row value={inr(bill.extraCharges)}>Extra charges</Row>}

        {gstEnabled && gstPercentage > 0 && (
          <Row value={inr(bill.gstAmount)}>GST ({gstPercentage}%)</Row>
        )}

        {bill.discount > 0 && (
          <Row value={`− ${inr(bill.discount)}`} tone="text-green-700" valueTone="text-green-700">
            Discount / Concession
          </Row>
        )}

        <div className="flex items-center justify-between pt-2 mt-1 border-t border-railway-300">
          <span className="font-semibold text-railway-800">{totalLabel}</span>
          <span className="text-xl font-bold text-railway-800">{inr(bill.totalAmount)}</span>
        </div>
      </div>
      {note && <p className="mt-2 text-xs text-railway-600">{note}</p>}
    </div>
  );
};

export default PriceSummary;
